// GeneralSettings.jsx
// -------------------------------------------------------------
// UI component for general settings
// -------------------------------------------------------------

import { useState, useEffect, useRef } from "react";
import { FiSettings, FiMapPin, FiLock, FiMail, FiShield, FiAlertTriangle } from "react-icons/fi";
import useSettings from "../hooks/useSettings";
import { useToast } from "../contexts/ToastContext";
import { useLicenseContext } from "../contexts/LicenseContext";
import { useI18n } from "../contexts/I18nContext";

export default function GeneralSettings({ highlightSetting = null, onHighlightDone }) {
  const { t } = useI18n();
  const {
    settings,
    updateSetting,
    loading,
    lockSettings,
    unlockSettings,
    resetSettings,
  } = useSettings();
  const { showToast } = useToast();
  const { license } = useLicenseContext();
  const isPaid = license?.lizenzTyp === "Paid";
  const defaultBetreff = t("Eine verschlüsselte E-Mail für Sie");

  const [betreffInput, setBetreffInput] = useState("");
  const [lockPassword, setLockPassword] = useState("");
  const [lockPasswordConfirm, setLockPasswordConfirm] = useState("");
  const [unlockPassword, setUnlockPassword] = useState("");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [activeHighlight, setActiveHighlight] = useState(highlightSetting);
  const highlightRef = useRef(null);

  useEffect(() => {
    if (!activeHighlight) return;
    highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => {
      setActiveHighlight(null);
      onHighlightDone?.();
    }, 2500);
    return () => clearTimeout(timer);
  }, [activeHighlight]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (settings?.vorlageBetreff) {
      setBetreffInput(settings.vorlageBetreff);
    }
  }, [settings?.vorlageBetreff]);

  if (!settings) {
    return <div>{t("Lädt Einstellungen...")}</div>;
  }

  const handleBetreffChange = (e) => setBetreffInput(e.target.value);

  const handleBetreffBlur = async () => {
    const finalValue = betreffInput.trim() || defaultBetreff;
    await updateSetting("vorlageBetreff", finalValue);
    setBetreffInput(finalValue);
  };

  const handleLockSettings = async () => {
    if (!lockPassword || !lockPasswordConfirm) {
      showToast(t("Bitte füllen Sie beide Passwortfelder aus."), "error");
      return;
    }
    if (lockPassword !== lockPasswordConfirm) {
      showToast(t("Die Passwörter stimmen nicht überein."), "error");
      return;
    }
    const success = await lockSettings(lockPassword, lockPasswordConfirm);
    if (success) {
      setLockPassword("");
      setLockPasswordConfirm("");
    }
  };

  const handleUnlockSettings = async () => {
    if (!unlockPassword) {
      showToast(t("Bitte geben Sie das Passwort ein."), "error");
      return;
    }
    const success = await unlockSettings(unlockPassword);
    if (success) setUnlockPassword("");
  };

  const handleResetSettings = async () => {
    if (showResetConfirm) {
      const success = await resetSettings();
      if (success) setShowResetConfirm(false);
    } else {
      setShowResetConfirm(true);
    }
  };

  return (
    <div style={cardStyle}>
      <h3><FiSettings size={16} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Allgemeine Einstellungen")}</h3>

      {/* Erinnerungen */}
      <h4><FiMapPin size={14} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Erinnerungen")}</h4>
      <Checkbox
        label={t("Immer ans Verschlüsseln erinnern")}
        value={settings.erinnerungVerschluesselnAlleEmpfaenger}
        onChange={(v) => updateSetting("erinnerungVerschluesselnAlleEmpfaenger", v)}
        disabled={loading || settings.passwortOptionenGuiDeaktivieren}
      />

      {/* Sicherheit */}
      <h4><FiLock size={14} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Sicherheit")}</h4>
      <div
        ref={activeHighlight === "nichtNachPasswortFragenFuerGespeicherteEmpfaenger" ? highlightRef : null}
        style={activeHighlight === "nichtNachPasswortFragenFuerGespeicherteEmpfaenger" ? {
          backgroundColor: "#fff3cd",
          border: "1px solid #ffc107",
          borderRadius: "6px",
          padding: "6px 8px",
          transition: "background 0.5s",
        } : {}}
      >
        <Checkbox
          label={t("Gespeicherte Passwörter ohne Nachfrage verwenden")}
          value={settings.nichtNachPasswortFragenFuerGespeicherteEmpfaenger}
          onChange={(v) => updateSetting("nichtNachPasswortFragenFuerGespeicherteEmpfaenger", v)}
          disabled={loading || settings.passwortOptionenGuiDeaktivieren}
        />
      </div>

      {/* E-Mail */}
      <h4><FiMail size={14} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("E-Mail")}</h4>
      <div style={{ marginBottom: "12px" }}>
        <label style={{ display: "block", marginBottom: "6px", fontWeight: "500" }}>
          {t("Betreff:")}
        </label>
        <input
          type="text"
          value={betreffInput}
          onChange={handleBetreffChange}
          onBlur={handleBetreffBlur}
          placeholder={defaultBetreff}
          disabled={loading || settings.passwortOptionenGuiDeaktivieren}
          style={inputStyle}
        />
      </div>
      <Checkbox
        label={t("Betreff nicht abändern")}
        value={settings.betreffUnverschluesseltBehalten}
        onChange={(v) => updateSetting("betreffUnverschluesseltBehalten", v)}
        disabled={loading || settings.passwortOptionenGuiDeaktivieren}
      />
      {/* NOTE: Previously "MSG-Datei einbetten" (legacy COM add-in). New Outlook/Web cannot
          produce MSG via Office.js — EML (RFC 822) is generated instead via getEmlBase64().
          Setting name kept as originalEmailEinbetten for backend compatibility. */}
      <Checkbox
        label={t("Original-E-Mail in PDF einbetten")}
        value={settings.originalEmailEinbetten}
        onChange={(v) => updateSetting("originalEmailEinbetten", v)}
        disabled={loading || settings.passwortOptionenGuiDeaktivieren}
      />
      {/* TODO: Re-enable once Sent Items copy is properly implemented (save during prepare, delete on failure)
      <Checkbox
        label="Original E-Mail unverschlüsselt unter gesendete Elemente speichern"
        value={settings.entschluesseltGesendetSpeichern}
        onChange={(v) => updateSetting("entschluesseltGesendetSpeichern", v)}
        disabled={loading || settings.passwortOptionenGuiDeaktivieren}
      />
      */}
      <Checkbox
        label={t("PDF Anhänge mit Nachfrage direkt verschlüsseln, wenn keine anderen Anhänge vorhanden sind")}
        value={settings.nurPdfDirektVerschluesseln}
        onChange={(v) => {
          if (!isPaid) {
            showToast(t("Diese Funktion ist nur in der Paid-Lizenz verfügbar."), "info");
            return;
          }
          updateSetting("nurPdfDirektVerschluesseln", v);
        }}
        disabled={loading || settings.passwortOptionenGuiDeaktivieren}
      />

      {/* Schutz & Verwaltung */}
      <h4><FiShield size={14} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Schutz & Verwaltung")}</h4>

      {!settings.passwortOptionenGuiDeaktivieren ? (
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
            {t("Passwort festlegen:")}
          </label>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
            <input
              type="password"
              placeholder={t("Passwort")}
              value={lockPassword}
              onChange={(e) => setLockPassword(e.target.value)}
              disabled={loading}
              style={{ flex: "1 1 180px", padding: "8px", borderRadius: "5px", border: "1px solid #ccc", fontSize: "13px" }}
            />
            <input
              type="password"
              placeholder={t("Bestätigung")}
              value={lockPasswordConfirm}
              onChange={(e) => setLockPasswordConfirm(e.target.value)}
              disabled={loading}
              style={{ flex: "1 1 180px", padding: "8px", borderRadius: "5px", border: "1px solid #ccc", fontSize: "13px" }}
            />
          </div>
          <button onClick={handleLockSettings} disabled={loading} style={btnPrimary(loading)}>
            {t("Einstellungen sperren")}
          </button>
        </div>
      ) : (
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
            {t("Passwort eingeben:")}
          </label>
          <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
            <input
              type="password"
              placeholder={t("Passwort")}
              value={unlockPassword}
              onChange={(e) => setUnlockPassword(e.target.value)}
              disabled={loading}
              style={{ flex: "1", padding: "8px", borderRadius: "5px", border: "1px solid #ccc", fontSize: "13px" }}
            />
          </div>
          <button onClick={handleUnlockSettings} disabled={loading} style={btnSuccess(loading)}>
            {t("Einstellungen freigeben")}
          </button>
        </div>
      )}

      {/* Reset */}
      <div style={{ marginTop: "16px" }}>
        <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
          {t("Einstellungen zurücksetzen:")}
        </label>
        {showResetConfirm ? (
          <div style={confirmBoxStyle}>
            <p style={{ margin: "0 0 10px 0", fontWeight: "500" }}><FiAlertTriangle size={14} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Sind Sie sicher?")}</p>
            <p style={{ margin: "0 0 10px 0", fontSize: "0.9em" }}>
              {t("Alle Einstellungen werden auf die Standardwerte zurückgesetzt. Diese Aktion kann nicht rückgängig gemacht werden.")}
            </p>
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={handleResetSettings} disabled={loading} style={btnDanger(loading)}>
                {t("Ja, zurücksetzen")}
              </button>
              <button onClick={() => setShowResetConfirm(false)} disabled={loading} style={btnGray}>
                {t("Abbrechen")}
              </button>
            </div>
          </div>
        ) : (
          <div>
              <button onClick={handleResetSettings} disabled={loading} style={{ ...btnDanger(loading), width: "100%" }}>
              {t("Einstellungen zurücksetzen")}
              </button>
          </div>
        )}
      </div>

    </div>
  );
}

function Checkbox({ label, value, onChange, disabled }) {
  return (
    <label style={{ display: "block", marginBottom: "10px", opacity: disabled ? 0.6 : 1 }}>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span style={{ marginLeft: "8px" }}>{label}</span>
    </label>
  );
}

const cardStyle = {
  backgroundColor: "#ffffff",
  borderRadius: "10px",
  border: "1px solid #e0e0e0",
  boxShadow: "0 4px 16px rgba(100,100,100,0.18)",
  padding: "16px 14px",
};

const inputStyle = {
  width: "100%",
  padding: "8px",
  borderRadius: "5px",
  border: "1px solid #ccc",
  fontSize: "13px",
  boxSizing: "border-box",
};

const confirmBoxStyle = {
  background: "#fff3cd",
  border: "1px solid #ffc107",
  padding: "12px",
  borderRadius: "6px",
  marginBottom: "8px",
};

const btnPrimary = (loading) => ({
  width: "100%",
  padding: "10px 24px",
  borderRadius: "6px",
  background: loading ? "#ccc" : "#0078d7",
  color: "#fff",
  border: "none",
  cursor: loading ? "not-allowed" : "pointer",
  fontWeight: "600",
  fontSize: "14px",
  whiteSpace: "nowrap",
});

const btnSuccess = (loading) => ({
  padding: "10px 24px",
  borderRadius: "6px",
  background: loading ? "#ccc" : "#28a745",
  color: "#fff",
  border: "none",
  cursor: loading ? "not-allowed" : "pointer",
  fontWeight: "600",
  fontSize: "14px",
  width: "100%",
});

const btnDanger = (loading) => ({
  padding: "10px 24px",
  borderRadius: "6px",
  background: loading ? "#ccc" : "#dc3545",
  color: "#fff",
  border: "none",
  cursor: loading ? "not-allowed" : "pointer",
  fontWeight: "600",
  fontSize: "14px",
});

const btnGray = {
  padding: "8px 16px",
  borderRadius: "6px",
  background: "#6c757d",
  color: "#fff",
  border: "none",
  cursor: "pointer",
};
